// PaymentLogos · v2.6.7 batch E (rev 2) — each chip uses the OFFICIAL brand
// background colour so every logo pops against a dark page background and
// reads cleanly on light themes too.
// v2.6.9 batch BD-3 · Added `size` prop. size="sm" shrinks chips by ~20%
// (used on the login screen for a quieter footer); default is the original
// page-footer size used on the customer dashboard.

function Chip({ children, testid, ariaLabel, bg, border = "border-white/20", w = "w-16", h = "h-11", transparent = false, translucent = false }) {
  // v2.6.9 batch BS · `transparent` strips the chip wrapper completely so
  // each logo SVG sits directly on the parent's background (used in the
  // marketing-site footer per the spec).
  // v2.6.9 batch BY · `translucent` keeps the rounded chip badge AND the
  // brand-coloured background, but renders it at 50 % opacity so the
  // chip lifts off the page while still tinting through to the dark
  // footer beneath. Mutually exclusive with `transparent`. Used in the
  // marketing footer per the latest payment-card-visibility spec.
  if (transparent && !translucent) {
    return (
      <div
        data-testid={testid}
        aria-label={ariaLabel}
        className={`${h} ${w} flex items-center justify-center transition-transform hover:scale-105`}
      >
        {children}
      </div>
    );
  }
  if (translucent) {
    return (
      <div
        data-testid={testid}
        aria-label={ariaLabel}
        className={`${h} ${w} relative rounded-md flex items-center justify-center hover:scale-105 transition-transform shadow-md overflow-hidden`}
      >
        {/* 50%-opacity tinted layer behind the icon — visual lift without
            crushing visibility on a dark background. */}
        <span aria-hidden="true" className={`absolute inset-0 ${bg} opacity-50 rounded-md`}/>
        <span aria-hidden="true" className="absolute inset-0 rounded-md border border-white/15"/>
        <span className="relative z-[1] flex items-center justify-center">{children}</span>
      </div>
    );
  }
  return (
    <div
      data-testid={testid}
      aria-label={ariaLabel}
      className={`${h} ${w} rounded-md ${bg} border ${border} flex items-center justify-center hover:scale-105 transition-transform shadow-md overflow-hidden`}
    >
      {children}
    </div>
  );
}

const VISA = (svgH) => (
  <svg viewBox="0 0 60 22" className={`${svgH} w-auto`} aria-hidden="true">
    <text x="2" y="17" fontFamily="Arial Black, Arial, Helvetica, sans-serif" fontWeight="900" fontSize="18" fontStyle="italic" fill="#fff" letterSpacing="0.5">VISA</text>
  </svg>
);

const MC = (svgH) => (
  <svg viewBox="0 0 40 24" className={`${svgH} w-auto`} aria-hidden="true">
    <circle cx="15" cy="12" r="10" fill="#eb001b"/>
    <circle cx="25" cy="12" r="10" fill="#f79e1b"/>
    <path d="M20 4 a10 10 0 0 1 0 16 a10 10 0 0 1 0 -16z" fill="#ff5f00"/>
  </svg>
);

const AMEX = (textSize) => (
  <span className={`text-white font-black tracking-[0.05em] ${textSize}`} style={{ fontFamily: "Arial Black, Arial, Helvetica, sans-serif" }}>AMEX</span>
);

const DISCOVER = (svgH) => (
  <svg viewBox="0 0 100 22" className={`${svgH} w-auto`} aria-hidden="true">
    <text x="0" y="16" fontFamily="Arial Black, Arial, Helvetica, sans-serif" fontWeight="900" fontSize="13" fill="#1a1a1a" letterSpacing="0.4">DISC</text>
    <circle cx="48" cy="11" r="7" fill="#f47216"/>
    <text x="60" y="16" fontFamily="Arial Black, Arial, Helvetica, sans-serif" fontWeight="900" fontSize="13" fill="#1a1a1a" letterSpacing="0.4">VER</text>
  </svg>
);

const APPLEPAY = (svgH) => (
  // Apple Pay official button — black bg with WHITE apple + "Pay"
  <svg viewBox="0 0 60 22" className={`${svgH} w-auto`} aria-hidden="true">
    <path d="M9.7 7.2c-.5.6-1.3 1.1-2.1 1-.1-.9.3-1.7.8-2.3.5-.6 1.4-1 2.1-1.1.1.9-.3 1.8-.8 2.4zm.8 1c-1.2-.1-2.1.7-2.7.7-.6 0-1.5-.7-2.4-.7-1.3 0-2.4.7-3 1.9-1.3 2.2-.3 5.4 1 7.2.6.9 1.4 1.8 2.4 1.8.9 0 1.3-.6 2.4-.6 1.2 0 1.5.6 2.4.6 1 0 1.7-.9 2.3-1.8.7-1 1-2 1-2-.1 0-2-.8-2.1-2.9 0-1.9 1.6-2.8 1.7-2.9-.9-1.4-2.3-1.5-2.7-1.5z" fill="#fff"/>
    <text x="17" y="16" fontFamily="-apple-system, BlinkMacSystemFont, Helvetica Neue, Helvetica, sans-serif" fontWeight="700" fontSize="14" fill="#fff" letterSpacing="-0.3">Pay</text>
  </svg>
);

const GPAY = (svgH) => (
  // Google Pay official button — WHITE bg, multicolour Google + dark "Pay"
  <svg viewBox="0 0 84 22" className={`${svgH} w-auto`} aria-hidden="true">
    <text x="0" y="16" fontFamily="Roboto, Arial, sans-serif" fontWeight="500" fontSize="14">
      <tspan fill="#4285f4" fontWeight="700">G</tspan>
      <tspan fill="#ea4335" fontWeight="700">o</tspan>
      <tspan fill="#fbbc04" fontWeight="700">o</tspan>
      <tspan fill="#4285f4" fontWeight="700">g</tspan>
      <tspan fill="#34a853" fontWeight="700">l</tspan>
      <tspan fill="#ea4335" fontWeight="700">e</tspan>
      <tspan dx="3" fill="#1a1a1a">Pay</tspan>
    </text>
  </svg>
);

const CASH = (textSize) => (
  <span className={`text-white font-black tracking-[0.18em] ${textSize}`} style={{ fontFamily: "Arial Black, Arial, Helvetica, sans-serif" }}>CASH</span>
);

export default function PaymentLogos({ className = "", light = false, size = "md", transparent = false, translucent = false }) {
  const headlineClass = light ? "text-white/85" : "text-zinc-500";
  // size="sm" → chips ~20% smaller (login footer). Default size="md" keeps the
  // existing customer-dashboard footer dimensions.
  const isSm = size === "sm";
  const h = isSm ? "h-9" : "h-11";
  const widths = isSm
    ? { w16: "w-12", w14: "w-11", w24: "w-20" }
    : { w16: "w-16", w14: "w-14", w24: "w-24" };
  const svgH = isSm ? "h-4" : "h-5";
  const mcH  = isSm ? "h-6" : "h-7";
  const amexText = isSm ? "text-[10px]" : "text-[12px]";
  const cashText = isSm ? "text-[11px]" : "text-[13px]";

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`} data-testid="payment-logos">
      {!transparent && (
        <div className={`text-[10px] uppercase tracking-[0.32em] font-semibold ${headlineClass}`}>Accepted Payment Methods</div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2.5">
        <Chip testid="logo-visa"       ariaLabel="Visa"             h={h} w={widths.w16} bg="bg-[#1a1f71]"    border="border-[#1a1f71]" transparent={transparent} translucent={translucent}>{VISA(svgH)}</Chip>
        <Chip testid="logo-mastercard" ariaLabel="Mastercard"       h={h} w={widths.w14} bg="bg-white"        border="border-zinc-300"  transparent={transparent} translucent={translucent}>{MC(mcH)}</Chip>
        <Chip testid="logo-amex"       ariaLabel="American Express" h={h} w={widths.w16} bg="bg-[#016fd0]"    border="border-[#016fd0]" transparent={transparent} translucent={translucent}>{AMEX(amexText)}</Chip>
        <Chip testid="logo-discover"   ariaLabel="Discover"         h={h} w={widths.w24} bg="bg-white"        border="border-zinc-300"  transparent={transparent} translucent={translucent}>{DISCOVER(svgH)}</Chip>
        <Chip testid="logo-applepay"   ariaLabel="Apple Pay"        h={h} w={widths.w16} bg="bg-black"        border="border-zinc-700"  transparent={transparent} translucent={translucent}>{APPLEPAY(svgH)}</Chip>
        <Chip testid="logo-googlepay"  ariaLabel="Google Pay"       h={h} w={widths.w24} bg="bg-white"        border="border-zinc-300"  transparent={transparent} translucent={translucent}>{GPAY(svgH)}</Chip>
        <Chip testid="logo-cash"       ariaLabel="Cash"             h={h} w={widths.w14} bg="bg-emerald-700"  border="border-emerald-600" transparent={transparent} translucent={translucent}>{CASH(cashText)}</Chip>
      </div>
    </div>
  );
}
